import { useState, useEffect, useCallback } from 'react';
import { configuratorIllustrationsAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Upload, Trash2, Image, Archive, Link, X, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveUrl = (url) => {
  if (!url) return '';
  return url.startsWith('/api') ? `${BACKEND_URL}${url}` : url;
};

export default function ConfiguratorIllustrationsSection() {
  const [reference, setReference] = useState({});
  const [garmentLabels, setGarmentLabels] = useState({});
  const [illustrations, setIllustrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeGarment, setActiveGarment] = useState('jacket');
  const [uploadingKey, setUploadingKey] = useState(null);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [urlDialogKey, setUrlDialogKey] = useState(null);
  const [urlInput, setUrlInput] = useState('');
  const [settingUrl, setSettingUrl] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [refRes, ilRes] = await Promise.all([
        configuratorIllustrationsAPI.reference(),
        configuratorIllustrationsAPI.list(),
      ]);
      setReference(refRes.data.reference || {});
      setGarmentLabels(refRes.data.garment_labels || {});
      setIllustrations(ilRes.data.illustrations || []);
      if (!refRes.data.reference?.[activeGarment]) {
        setActiveGarment(Object.keys(refRes.data.reference || {})[0] || 'jacket');
      }
    } catch (err) {
      toast.error('Failed to load illustration data');
    } finally {
      setLoading(false);
    }
  }, [activeGarment]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const illustrationMap = {};
  for (const il of illustrations) {
    illustrationMap[`${il.garment_type}|${il.parameter}|${il.option_value}`] = il;
  }

  const handleUpload = async (garmentType, parameter, optionValue, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const key = `${garmentType}|${parameter}|${optionValue}`;
    setUploadingKey(key);
    try {
      await configuratorIllustrationsAPI.upload(garmentType, parameter, optionValue, file);
      toast.success(`Uploaded illustration for ${optionValue}`);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingKey(null);
    }
  };

  const handleDelete = async (id, optionValue) => {
    try {
      await configuratorIllustrationsAPI.remove(id);
      toast.success(`Removed illustration for ${optionValue}`);
      await fetchData();
    } catch (err) {
      toast.error('Failed to remove');
    }
  };

  const handleSetUrl = async () => {
    if (!urlDialogKey || !urlInput.trim()) return;
    const [garmentType, parameter, optionValue] = urlDialogKey.split('|');
    setSettingUrl(true);
    try {
      await configuratorIllustrationsAPI.setUrl({ garment_type: garmentType, parameter, option_value: optionValue, url: urlInput.trim() });
      toast.success(`Set URL for ${optionValue}`);
      setUrlDialogKey(null);
      setUrlInput('');
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set URL');
    } finally {
      setSettingUrl(false);
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) return;
    setBulkUploading(true);
    try {
      const res = await configuratorIllustrationsAPI.bulkUpload(bulkFile);
      setBulkResult(res.data);
      toast.success(`Saved ${res.data.saved} illustrations`);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk upload failed');
    } finally {
      setBulkUploading(false);
    }
  };

  const garmentTypes = Object.keys(reference);
  const currentParams = reference[activeGarment] || {};

  const getParamStats = (gt) => {
    let total = 0, filled = 0;
    const params = reference[gt] || {};
    for (const pk of Object.keys(params)) {
      for (const opt of params[pk].options) {
        total++;
        if (illustrationMap[`${gt}|${pk}|${opt.value}`]) filled++;
      }
    }
    return { total, filled };
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="configurator-illustrations-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold" style={{ color: 'var(--brand-navy)' }}>Configurator Illustrations</h2>
          <p className="text-xs mt-0.5" style={{ color: 'var(--ink-muted)' }}>
            Upload custom images for garment configurator options
          </p>
        </div>
        <Button data-testid="bulk-upload-button" onClick={() => { setBulkDialogOpen(true); setBulkResult(null); setBulkFile(null); }}
          className="h-10 rounded-xl text-sm gap-2" style={{ backgroundColor: 'var(--brand-navy)', color: 'white' }}>
          <Archive className="w-4 h-4" /> Bulk Upload (ZIP)
        </Button>
      </div>

      {/* Garment Type Tabs */}
      <div className="flex gap-1.5 flex-wrap" data-testid="garment-type-tabs">
        {garmentTypes.map(gt => {
          const stats = getParamStats(gt);
          const isActive = gt === activeGarment;
          return (
            <button key={gt} onClick={() => setActiveGarment(gt)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all"
              style={{
                backgroundColor: isActive ? 'var(--brand-navy)' : 'rgba(27,42,74,0.04)',
                color: isActive ? 'white' : 'var(--ink-muted)',
              }}
              data-testid={`illustration-tab-${gt}`}>
              {garmentLabels[gt] || gt}
              {stats.filled > 0 && (
                <Badge className="text-[8px] px-1 py-0 rounded-full"
                  style={{ backgroundColor: isActive ? 'rgba(255,255,255,0.2)' : 'rgba(197,165,90,0.15)', color: isActive ? 'white' : '#92700C' }}>
                  {stats.filled}/{stats.total}
                </Badge>
              )}
            </button>
          );
        })}
      </div>

      {/* Parameter Sections */}
      {Object.entries(currentParams).map(([paramKey, paramData]) => (
        <Card key={paramKey} className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--brand-navy)' }}>{paramData.label}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
              {paramData.options.map(opt => {
                const mapKey = `${activeGarment}|${paramKey}|${opt.value}`;
                const illustration = illustrationMap[mapKey];
                const isUploading = uploadingKey === mapKey;

                return (
                  <div key={opt.value}
                    className="relative rounded-xl border-2 overflow-hidden transition-all group"
                    style={{ borderColor: illustration ? 'rgba(197,165,90,0.4)' : 'rgba(27,42,74,0.08)' }}
                    data-testid={`illustration-card-${activeGarment}-${paramKey}-${opt.value}`}>
                    {/* Image area */}
                    <div className="aspect-square bg-gray-50 flex items-center justify-center relative overflow-hidden">
                      {illustration ? (
                        <>
                          <img src={resolveUrl(illustration.image_url)} alt={opt.label}
                            className="w-full h-full object-contain p-1" />
                          {/* Hover overlay */}
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            <button onClick={() => handleDelete(illustration.id, opt.value)}
                              className="p-1.5 rounded-full bg-white/20 backdrop-blur-sm hover:bg-red-500/40 transition-colors"
                              data-testid={`delete-illustration-${opt.value}`}>
                              <Trash2 className="w-3.5 h-3.5 text-white" />
                            </button>
                            <label className="p-1.5 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/40 transition-colors cursor-pointer">
                              <Upload className="w-3.5 h-3.5 text-white" />
                              <input type="file" accept="image/*" className="hidden"
                                onChange={(e) => handleUpload(activeGarment, paramKey, opt.value, e)} />
                            </label>
                          </div>
                        </>
                      ) : (
                        <div className="flex flex-col items-center gap-2 p-2">
                          <Image className="w-8 h-8 opacity-15" />
                          <div className="flex gap-1">
                            <label className={`p-1.5 rounded-lg border border-border/60 cursor-pointer hover:bg-gray-100 transition-colors ${isUploading ? 'opacity-50' : ''}`}>
                              <Upload className="w-3.5 h-3.5" style={{ color: 'var(--ink-muted)' }} />
                              <input type="file" accept="image/*" className="hidden" disabled={isUploading}
                                onChange={(e) => handleUpload(activeGarment, paramKey, opt.value, e)} />
                            </label>
                            <button onClick={() => { setUrlDialogKey(mapKey); setUrlInput(''); }}
                              className="p-1.5 rounded-lg border border-border/60 hover:bg-gray-100 transition-colors">
                              <Link className="w-3.5 h-3.5" style={{ color: 'var(--ink-muted)' }} />
                            </button>
                          </div>
                        </div>
                      )}
                      {isUploading && (
                        <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                          <div className="w-5 h-5 border-2 border-[var(--brand-gold)] border-t-transparent rounded-full animate-spin" />
                        </div>
                      )}
                    </div>
                    {/* Label */}
                    <div className="px-2 py-1.5 text-center border-t" style={{ borderColor: 'rgba(27,42,74,0.06)' }}>
                      <p className="text-[10px] font-semibold truncate" style={{ color: 'var(--brand-navy)' }}>{opt.label}</p>
                      <p className="text-[8px]" style={{ color: illustration ? '#059669' : 'var(--ink-muted)' }}>
                        {illustration ? 'Custom' : 'SVG fallback'}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}

      {/* URL Dialog */}
      <Dialog open={!!urlDialogKey} onOpenChange={(open) => !open && setUrlDialogKey(null)}>
        <DialogContent className="max-w-md" data-testid="illustration-url-dialog">
          <h3 className="text-base font-semibold mb-2" style={{ color: 'var(--brand-navy)' }}>Set Image URL</h3>
          <p className="text-xs mb-3" style={{ color: 'var(--ink-muted)' }}>
            Paste the URL of the illustration image
          </p>
          <div className="flex gap-2">
            <Input value={urlInput} onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://example.com/illustration.png"
              className="h-10 rounded-lg text-sm flex-1"
              data-testid="illustration-url-input" />
            <Button onClick={handleSetUrl} disabled={settingUrl || !urlInput.trim()}
              className="h-10 rounded-lg px-4" style={{ backgroundColor: 'var(--brand-navy)', color: 'white' }}
              data-testid="illustration-url-save">
              {settingUrl ? '...' : 'Set'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={(open) => !open && setBulkDialogOpen(false)}>
        <DialogContent className="max-w-lg" data-testid="bulk-upload-dialog">
          <h3 className="text-base font-semibold mb-1" style={{ color: 'var(--brand-navy)' }}>Bulk Upload Illustrations</h3>
          <p className="text-xs mb-3" style={{ color: 'var(--ink-muted)' }}>
            Upload a ZIP file. Files are auto-mapped by filename convention:<br/>
            <code className="text-[10px] px-1 py-0.5 rounded bg-gray-100">jacket/lapel_style_notch.png</code> or{' '}
            <code className="text-[10px] px-1 py-0.5 rounded bg-gray-100">jacket_lapel_style_notch.png</code>
          </p>

          {!bulkResult ? (
            <div className="space-y-3">
              <div className="border-2 border-dashed border-border/60 rounded-xl p-6 text-center cursor-pointer hover:border-[color:var(--brand-gold)]"
                onClick={() => document.getElementById('bulk-zip-upload').click()}>
                <input type="file" accept=".zip" onChange={(e) => { setBulkFile(e.target.files?.[0] || null); }}
                  className="hidden" id="bulk-zip-upload" data-testid="bulk-zip-input" />
                {bulkFile ? (
                  <p className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>{bulkFile.name}</p>
                ) : (
                  <div className="flex flex-col items-center gap-1">
                    <Archive className="w-8 h-8 opacity-20" />
                    <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Click to select ZIP file</p>
                  </div>
                )}
              </div>
              <Button onClick={handleBulkUpload} disabled={!bulkFile || bulkUploading}
                className="w-full h-11 rounded-xl" style={{ backgroundColor: 'var(--brand-navy)', color: 'white' }}
                data-testid="bulk-upload-confirm">
                {bulkUploading ? 'Processing...' : 'Upload & Map'}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center p-3 rounded-lg" style={{ backgroundColor: 'rgba(30,122,95,0.06)' }}>
                  <p className="text-xl font-semibold" style={{ color: 'var(--success)' }}>{bulkResult.saved}</p>
                  <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Saved</p>
                </div>
                <div className="text-center p-3 rounded-lg" style={{ backgroundColor: 'rgba(245,158,11,0.06)' }}>
                  <p className="text-xl font-semibold" style={{ color: '#D97706' }}>{bulkResult.unmapped_count}</p>
                  <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Unmapped</p>
                </div>
              </div>
              {bulkResult.unmapped_files?.length > 0 && (
                <div className="p-3 rounded-lg text-xs max-h-32 overflow-auto" style={{ backgroundColor: 'rgba(245,158,11,0.06)' }}>
                  <p className="font-semibold mb-1" style={{ color: '#D97706' }}>Unmapped files:</p>
                  {bulkResult.unmapped_files.map((f, i) => (
                    <p key={i} className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>{f}</p>
                  ))}
                </div>
              )}
              {bulkResult.mappings?.length > 0 && (
                <div className="max-h-40 overflow-auto rounded-lg border p-2">
                  {bulkResult.mappings.slice(0, 15).map((m, i) => (
                    <div key={i} className="flex items-center justify-between py-1 text-xs">
                      <span style={{ color: 'var(--navy-ink)' }}>
                        {m.garment_type} / {m.parameter} / {m.option_value}
                      </span>
                      <Check className="w-3.5 h-3.5" style={{ color: 'var(--success)' }} />
                    </div>
                  ))}
                </div>
              )}
              <Button onClick={() => setBulkDialogOpen(false)} className="w-full h-10 rounded-xl"
                style={{ backgroundColor: 'var(--brand-navy)', color: 'white' }}>
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
